import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import { PREVIEW_DATA } from 'src/utils/gql/queries';

interface PreviewTableProps {
  connectionId: string;
  schema: string;
  table: string;
  fields: string[];
}

export const PreviewTable: React.FC<PreviewTableProps> = ({
  connectionId,
  schema,
  table,
  fields,
}) => {
  const { data, loading, error } = useQuery(PREVIEW_DATA, {
    variables: { connectionId, schema, table, fields },
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Errore nel caricamento dell'anteprima: {error.message}
      </Alert>
    );
  }

  const previewData = data?.previewData || [];
  const parsedData = previewData.map((item: any) => JSON.parse(item.data));

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        👁️ Anteprima Dati
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        gutterBottom
        sx={{ mb: 3 }}
      >
        Tabella:{' '}
        <strong>
          {schema}.{table}
        </strong>{' '}
        • Campi: <code>{fields.join(', ')}</code>
      </Typography>

      <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {fields.map((field) => (
                  <TableCell
                    key={field}
                    sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                  >
                    {field}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {parsedData.map((row: any, index: number) => (
                <TableRow
                  key={index}
                  sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}
                >
                  {fields.map((field) => (
                    <TableCell key={field}>
                      {row[field] !== null && row[field] !== undefined ? (
                        String(row[field]).substring(0, 100)
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>
                          NULL
                        </span>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Mostrando <strong>{parsedData.length}</strong> righe di anteprima
      </Typography>
    </Box>
  );
};
